import React, { useEffect, useState } from 'react';
import { Box, Button, Chip, Divider, Typography } from '@mui/material';
import ArrowBackIos from '@mui/icons-material/ArrowBackIos';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import Api from '../../api';
import Loader from '../../components/shared/Loader';
import {
  mainColors,
  secondaryColors,
  successColors,
  warningColors
} from '../../theme/themeConfig';
import {
  MainContainer,
  RowContainer,
  WhiteContainer
} from './TechnicianTaskDetails';
import useNotificationsStore from '../../store/zustand/notifications';
import { type ServiceRequestDTO } from '../../api/serviceRequest';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDuration(startIso: string, endIso?: string): string {
  const ms =
    (endIso ? new Date(endIso) : new Date()).getTime() -
    new Date(startIso).getTime();
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function fmtTime(iso: string) {
  return new Date(iso).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit'
  });
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  onBack: () => void;
  onCompleted?: () => void;
  onStatusChange?: (status: string) => void;
  serviceRequestId: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

const SRTaskDetails: React.FC<Props> = function SRTaskDetails({
  onBack,
  onCompleted,
  onStatusChange,
  serviceRequestId
}) {
  const { addNotification } = useNotificationsStore((s) => s);
  const [sr, setSR] = useState<ServiceRequestDTO | null>(null);
  const [assignment, setAssignment] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [elapsed, setElapsed] = useState('');

  // ─── Fetch ──────────────────────────────────────────────────────────────────

  const loadSR = async () => {
    const data = await Api.ServiceRequestApi.getById(serviceRequestId);
    setSR(data);
    const a = (data.assignments ?? []).find(
      (x: any) => x.clockedInAt !== undefined || x.scheduledStart !== undefined
    );
    setAssignment(a ?? null);
    if (data.status === 'in_progress') onStatusChange?.('in_progress');
    return data;
  };

  useEffect(() => {
    loadSR()
      .catch(() => addNotification('Failed to load service request', 'error'))
      .finally(() => setLoading(false));
  }, [serviceRequestId]);

  // ─── Live timer ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (sr?.status !== 'in_progress' || !assignment?.clockedInAt) {
      return undefined;
    }
    const update = () => setElapsed(formatDuration(assignment.clockedInAt));
    update();
    const id = setInterval(update, 30000);
    return () => clearInterval(id);
  }, [sr?.status, assignment?.clockedInAt]);

  // ─── Actions ─────────────────────────────────────────────────────────────────

  const handleAction = async (action: () => Promise<any>, msg: string) => {
    setActionLoading(true);
    try {
      await action();
      addNotification(msg, 'success');
      const updated = await loadSR();
      onStatusChange?.(updated.status);
      if (updated.status === 'completed') onCompleted?.();
    } catch (e: any) {
      addNotification(e?.response?.data?.message || 'Action failed', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // ─── Date gate ───────────────────────────────────────────────────────────────

  const scheduledDate = assignment?.scheduledStart
    ? new Date(assignment.scheduledStart)
    : null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let isScheduledDateToday = true;
  if (scheduledDate) {
    const sd = new Date(scheduledDate);
    sd.setHours(0, 0, 0, 0);
    isScheduledDateToday = sd <= today;
  }

  // ─── Loading / error ─────────────────────────────────────────────────────────

  if (loading) {
    return (
      <MainContainer>
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: '60px' }}>
          <Loader customColor="primary" />
        </Box>
      </MainContainer>
    );
  }

  if (!sr) {
    return (
      <MainContainer>
        <RowContainer>
          <Box
            onClick={onBack}
            sx={{
              alignItems: 'center',
              cursor: 'pointer',
              display: 'flex',
              gap: 1
            }}
          >
            <ArrowBackIos sx={{ color: mainColors.MAIN, fontSize: 18 }} />
            <Typography fontWeight={700}>Service Request</Typography>
          </Box>
        </RowContainer>
        <WhiteContainer>
          <Typography sx={{ color: 'red', fontSize: '13px' }}>
            Could not load service request (ID: {serviceRequestId || 'missing'}
            ).
          </Typography>
        </WhiteContainer>
      </MainContainer>
    );
  }

  const isClockedIn = sr.status === 'in_progress' && !assignment?.clockedOutAt;
  const isCompleted = sr.status === 'completed' || sr.status === 'invoiced';
  const isClockedOut =
    !!assignment?.clockedOutAt && !isCompleted;
  const canClockIn =
    isScheduledDateToday &&
    !isClockedOut &&
    (sr.status === 'dispatched' || sr.status === 'authorized');
  const canClockOut = isClockedIn;
  const canComplete = isScheduledDateToday && isClockedOut;

  const clientName =
    sr.client?.businessName ||
    [sr.client?.firstName, sr.client?.lastName].filter(Boolean).join(' ') ||
    '';

  let statusColor: 'warning' | 'success' | 'primary' = 'primary';
  if (isClockedIn) statusColor = 'warning';
  else if (isCompleted) statusColor = 'success';
  let statusLabel = 'Incoming';
  if (isClockedIn) statusLabel = 'In progress';
  else if (isClockedOut) statusLabel = 'Clocked out';
  else if (isCompleted) statusLabel = 'Completed';

  return (
    <MainContainer>
      {/* Header — matches TechnicianTaskDetails exactly */}
      <RowContainer>
        <Typography color="primary.400" fontSize={24} variant="heading_300">
          <ArrowBackIos
            fill="grey"
            onClick={onBack}
            style={{ cursor: 'pointer', marginBottom: '-4px' }}
          />
          Task details
        </Typography>
      </RowContainer>

      {/* Visit Details card — matches Image 2 layout */}
      <WhiteContainer>
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, mb: 1 }}>
          <Chip color={statusColor} label={statusLabel} size="small" />
        </Box>

        <Typography fontWeight={700} sx={{ fontSize: '16px', mb: '12px' }}>
          {sr.title?.replace(/^SR:\s*/i, '').trim()}
        </Typography>

        {/* Address */}
        {(sr as any).client?.addresses?.[0]?.address && (
          <Box
            sx={{
              alignItems: 'flex-start',
              display: 'flex',
              gap: 1,
              mb: '8px'
            }}
          >
            <LocationOnOutlinedIcon
              sx={{ color: secondaryColors.SECONDARY, fontSize: 18, mt: '2px' }}
            />
            <Typography
              sx={{ color: secondaryColors.SECONDARY, fontSize: '13px' }}
            >
              {(sr as any).client?.addresses?.[0]?.address}
            </Typography>
          </Box>
        )}

        {/* Scheduled time */}
        {assignment?.scheduledStart && (
          <Box
            sx={{ alignItems: 'center', display: 'flex', gap: 1, mb: '8px' }}
          >
            <AccessTimeIcon
              sx={{ color: secondaryColors.SECONDARY, fontSize: 18 }}
            />
            <Typography
              sx={{ color: secondaryColors.SECONDARY, fontSize: '13px' }}
            >
              {new Date(assignment.scheduledStart).toLocaleDateString('en-US', {
                day: 'numeric',
                month: 'short',
                weekday: 'short'
              })}{' '}
              {fmtTime(assignment.scheduledStart)}
              {assignment.scheduledEnd &&
                ` - ${fmtTime(assignment.scheduledEnd)}`}
            </Typography>
          </Box>
        )}

        {/* Client */}
        {clientName && (
          <Box
            sx={{ alignItems: 'center', display: 'flex', gap: 1, mb: '8px' }}
          >
            <PersonOutlineIcon
              sx={{ color: secondaryColors.SECONDARY, fontSize: 18 }}
            />
            <Typography
              sx={{ color: secondaryColors.SECONDARY, fontSize: '13px' }}
            >
              {clientName}
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: '12px' }} />

        {/* Time tracking — matches existing travel/current/pause row */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <Box sx={{ display: 'flex' }}>
            <Typography
              sx={{ fontSize: '12px', fontWeight: 700, minWidth: '110px' }}
            >
              Scheduled time:
            </Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: 700 }}>
              {sr.estimatedDurationMinutes
                ? `${sr.estimatedDurationMinutes} min`
                : '--'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex' }}>
            <Typography
              sx={{ fontSize: '12px', fontWeight: 700, minWidth: '110px' }}
            >
              Current time:
            </Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: 700 }}>
              {(() => {
                if (isClockedIn && elapsed) return elapsed;
                if (assignment?.clockedInAt && assignment?.clockedOutAt) {
                  return formatDuration(
                    assignment.clockedInAt,
                    assignment.clockedOutAt
                  );
                }
                return '--';
              })()}
            </Typography>
          </Box>
        </Box>

        {/* Live timer — matches "Time on task" banner in Image 2 */}
        {isClockedIn && elapsed && (
          <Box
            sx={{
              background: warningColors.LIGHT_ERROR,
              borderRadius: '6px',
              mt: '10px',
              p: '8px',
              textAlign: 'center'
            }}
          >
            <Typography
              sx={{
                color: warningColors.ERROR,
                fontSize: '13px',
                fontWeight: 700
              }}
            >
              ⏱ Time on task: {elapsed}
            </Typography>
          </Box>
        )}
      </WhiteContainer>

      {/* Scope of work — description only, no prices */}
      {((sr.lineItems ?? []).length > 0 || sr.description) && (
        <WhiteContainer>
          {sr.description && (
            <Typography
              sx={{
                fontSize: '13px',
                lineHeight: 1.6,
                mb: (sr.lineItems ?? []).length > 0 ? '12px' : 0
              }}
            >
              {sr.description}
            </Typography>
          )}

          {(sr.lineItems ?? []).length > 0 && (
            <>
              <Typography
                sx={{
                  color: secondaryColors.SECONDARY,
                  fontSize: '11px',
                  fontWeight: 700,
                  letterSpacing: '.5px',
                  mb: '8px',
                  textTransform: 'uppercase'
                }}
              >
                Scope of Work
              </Typography>
              {(sr.lineItems ?? []).map((item: any) => (
                <Box key={item.id} sx={{ mb: '6px' }}>
                  <Typography sx={{ fontSize: '13px' }}>
                    {item.description}
                    {item.quantity && Number(item.quantity) !== 1 && (
                      <Box
                        component="span"
                        sx={{ color: '#94A3B8', ml: '4px' }}
                      >
                        × {Number(item.quantity)}
                        {item.unit ? ` ${item.unit}` : ''}
                      </Box>
                    )}
                    {item.unit &&
                      (!item.quantity || Number(item.quantity) === 1) && (
                        <Box
                          component="span"
                          sx={{ color: '#94A3B8', ml: '4px' }}
                        >
                          / {item.unit}
                        </Box>
                      )}
                  </Typography>
                </Box>
              ))}
            </>
          )}

          {/* Operator instructions */}
          {sr.operatorNotes && (
            <>
              <Divider sx={{ my: '12px' }} />
              <Box
                sx={{
                  background: '#FFFBEB',
                  borderRadius: '6px',
                  color: '#92400E',
                  fontSize: '12px',
                  lineHeight: 1.6,
                  p: '8px 12px'
                }}
              >
                {sr.operatorNotes}
              </Box>
            </>
          )}
        </WhiteContainer>
      )}

      {/* Date gate message */}
      {!isCompleted && !isScheduledDateToday && scheduledDate && (
        <WhiteContainer sx={{ pb: '24px', textAlign: 'center' }}>
          <Typography
            sx={{ color: secondaryColors.SECONDARY, fontSize: '13px' }}
          >
            Scheduled for{' '}
            <strong>
              {scheduledDate.toLocaleDateString('en-US', {
                day: 'numeric',
                month: 'short',
                weekday: 'short'
              })}
            </strong>
            . You can start it on that day.
          </Typography>
        </WhiteContainer>
      )}

      {/* Actions — matches existing Take a Break / Clock out layout */}
      {!isCompleted && isScheduledDateToday && (
        <WhiteContainer
          sx={{
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            pb: '24px',
            position: 'sticky'
          }}
        >
          {canClockIn && (
            <Button
              fullWidth
              disabled={actionLoading}
              onClick={() =>
                handleAction(
                  () => Api.ServiceRequestApi.clockIn(sr.id),
                  'Clocked in — timer started'
                )
              }
              sx={{ alignItems: 'center', display: 'flex', gap: '1rem' }}
              variant="primary"
            >
              {actionLoading ? (
                <Loader customColor="white" customSize={20} />
              ) : (
                'Start task'
              )}
            </Button>
          )}
          {canClockOut && (
            <Button
              fullWidth
              disabled={actionLoading}
              onClick={() =>
                handleAction(
                  () => Api.ServiceRequestApi.clockOut(sr.id),
                  'Clocked out'
                )
              }
              sx={{ alignItems: 'center', display: 'flex', gap: '1rem' }}
              variant="outlined"
            >
              {actionLoading ? (
                <Loader customColor="primary" customSize={20} />
              ) : (
                'Clock out'
              )}
            </Button>
          )}
          {canComplete && (
            <Button
              fullWidth
              disabled={actionLoading}
              onClick={() =>
                handleAction(
                  () => Api.ServiceRequestApi.complete(sr.id),
                  'Service request completed'
                )
              }
              sx={{
                alignItems: 'center',
                background: successColors.SUCCESS,
                display: 'flex',
                gap: '1rem'
              }}
              variant="contained"
            >
              {actionLoading ? (
                <Loader customColor="white" customSize={20} />
              ) : (
                'Completed'
              )}
            </Button>
          )}
        </WhiteContainer>
      )}

      {isCompleted && (
        <WhiteContainer sx={{ pb: '24px', textAlign: 'center' }}>
          <Typography
            sx={{
              color: successColors.SUCCESS,
              fontSize: '14px',
              fontWeight: 700
            }}
          >
            ✓ Service Request Completed
          </Typography>
          {assignment?.clockedInAt && (
            <Typography
              sx={{
                color: secondaryColors.SECONDARY,
                fontSize: '12px',
                mt: '4px'
              }}
            >
              Total time:{' '}
              {formatDuration(assignment.clockedInAt, assignment.clockedOutAt)}
            </Typography>
          )}
        </WhiteContainer>
      )}
    </MainContainer>
  );
};

SRTaskDetails.defaultProps = {
  onCompleted: undefined,
  onStatusChange: undefined
};

export default SRTaskDetails;
